import type { Panel, PanelRow, Section } from "@electro-planner/domain";

/**
 * Excel-export. Laddar `xlsx` dynamiskt så första-gångsbesökare inte betalar
 * bundle-kostnaden. Skickar ut en enda sheet med centralspec-kolumnerna.
 */
export async function exportPanelToXlsx({
  panel,
  sections,
  rows,
}: {
  panel: Panel | undefined;
  sections: ReadonlyArray<Section>;
  rows: ReadonlyArray<PanelRow>;
}): Promise<void> {
  const XLSX = await import("xlsx");
  const sectionMap = new Map(sections.map((s) => [s.id, s]));
  const sortedRows = [...rows].sort((a, b) => a.order - b.order);

  const aoa: (string | number | null)[][] = [
    [
      "Granskad",
      "Beteckning",
      "Komponent",
      "A",
      "Pol",
      "Karakteristik",
      "Känslighet (mA)",
      "JFB-typ",
      "Kabeltyp",
      "Ledare",
      "Area (mm²)",
      "Längd (m)",
      "Kabelnr",
      "Sektion",
      "Användning",
      "Last (W)",
      "Notering",
    ],
    ...sortedRows.map((r) => [
      r.reviewed ? "✓" : "",
      r.designation,
      r.component ?? "",
      r.ampRating ?? null,
      r.poles ?? null,
      r.characteristic ?? "",
      r.sensitivity ?? null,
      r.rcdType ?? "",
      r.cableType ?? "",
      r.cableCores ?? null,
      r.cableArea ?? null,
      r.cableLength ?? null,
      r.cableNumber ?? "",
      r.sectionId ? sectionMap.get(r.sectionId)?.code ?? "" : "",
      r.usage ?? "",
      r.loadWatts ?? null,
      r.notes ?? "",
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [
    { wch: 4 },
    { wch: 10 },
    { wch: 20 },
    { wch: 6 },
    { wch: 5 },
    { wch: 8 },
    { wch: 12 },
    { wch: 8 },
    { wch: 10 },
    { wch: 8 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 24 },
    { wch: 10 },
    { wch: 24 },
  ];
  const wb = XLSX.utils.book_new();
  const sheetName =
    (panel?.designation || "Central").slice(0, 28).replace(/[:\\/?*[\]]/g, "") ||
    "Central";
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const filename = `${sanitize(panel?.designation ?? "central")}-${sanitize(
    panel?.title ?? "spec",
  )}.xlsx`;
  XLSX.writeFile(wb, filename);
}

function sanitize(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "central";
}
